import { getApiBaseUrl } from './getEnv';

/** Outcome of probing the API for session liveness. */
export type SessionProbeResult = 'alive' | 'expired' | 'unreachable';

const PROBE_BODY = JSON.stringify({
  operationName: 'SessionExpiryProbe',
  query: 'query SessionExpiryProbe { getCurrentSession { id } }',
});

/**
 * Asks the API whether the current session cookie still authenticates.
 *
 * Uses a raw fetch rather than the Apollo client so the probe cannot recurse
 * through the error link that triggers it. The server rejects session-less
 * GraphQL requests with HTTP 401 before executing the document, so the status
 * code alone is decisive: 401 → expired, 2xx → alive, anything else (5xx,
 * network failure) → unreachable.
 *
 * @param fetchFn Injectable fetch implementation; defaults to the global.
 */
export const probeSession = async (fetchFn: typeof fetch = fetch): Promise<SessionProbeResult> => {
  try {
    const response = await fetchFn(`${getApiBaseUrl()}/graphql`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: PROBE_BODY,
    });
    if (response.status === 401) {
      return 'expired';
    }
    return response.ok ? 'alive' : 'unreachable';
  } catch {
    return 'unreachable';
  }
};

interface ICreateUnauthorizedHandlerOptions {
  /** Probes the API for session liveness. */
  probe: () => Promise<SessionProbeResult>;
  /** `true` when the viewer is already on the login route. */
  isOnLoginRoute: () => boolean;
  /** Tear-down path: clear local state and send the viewer to login. */
  onConfirmedExpired: () => void;
  /** Observability hook for unauthorized responses that did not confirm expiry. */
  onTransientUnauthorized: (operationName: string, probeResult: SessionProbeResult) => void;
}

/**
 * Builds the handler the Apollo error link invokes on unauthorized responses.
 *
 * A 401 is not always a dead session — a request can race session-row churn
 * (an expiring impersonation session, a recycled staging session) and lose
 * while the cookie is still valid. Ejecting on that false positive clears the
 * Apollo store and hard-redirects mid-flow, destroying in-progress UI state.
 * So: probe once (single-flight across concurrent 401s) and only tear down
 * when the probe itself comes back 401. Unconfirmed outcomes leave the viewer
 * in place — the failed operation still surfaces its own error, and a
 * genuinely dead session re-probes on the next unauthorized response.
 */
export const createUnauthorizedHandler = (
  options: ICreateUnauthorizedHandlerOptions
): ((operationName: string) => void) => {
  const { probe, isOnLoginRoute, onConfirmedExpired, onTransientUnauthorized } = options;
  let probeInFlight: Promise<void> | null = null;
  let confirmedExpired = false;

  return (operationName: string) => {
    if (confirmedExpired || probeInFlight !== null || isOnLoginRoute()) {
      return;
    }
    probeInFlight = probe()
      .catch((): SessionProbeResult => 'unreachable')
      .then((result) => {
        if (result === 'expired') {
          confirmedExpired = true;
          onConfirmedExpired();
          return;
        }
        onTransientUnauthorized(operationName, result);
      })
      .finally(() => {
        probeInFlight = null;
      });
  };
};
